<template>
  <div class="cadastrar-form">
    <Breadcrumb :items="breadcrumbItems" />
    <div class="titulo-form">
      <h3>
        {{
          modoAdicao
            ? "Adicionar Encontros"
            : isEdicao
              ? "Edição de Encontros"
              : "Cadastro de Encontros"
        }}
      </h3>
    </div>

    <div class="form-layout">
      <div class="col-left">
        <FormEncontroInputs
          v-model="encontroAtual"
          :data-minima="dataMinima"
          @adicionar="adicionarEncontro"
        />
      </div>

      <div class="col-right">
        <ListaEncontrosAdicionados
          :encontros="encontrosAdicionados"
          @editar="editarEncontro"
          @remover="removerEncontro"
        />
      </div>
    </div>

    <div class="cadastro-form-container">
      <button
        @click="voltar"
        class="voltar-btn"
        type="button"
      >
        Voltar
      </button>

      <button
        v-if="isEdicao && !modoAdicao"
        @click="restaurarEncontrosOriginais"
        class="restaurar-btn"
        type="button"
        :disabled="isSaving || isSavingEncontros || isLoadingEncontros"
      >
        {{ isLoadingEncontros ? "Carregando..." : "Restaurar dados originais" }}
      </button>

      <div class="cadastro-form">
        <button
          type="button"
          @click="cadastrarEncontros"
          :disabled="
            encontrosAdicionados.length === 0 || isSaving || isSavingEncontros
          "
        >
          {{
            isSaving || isSavingEncontros
              ? "Salvando..."
              : modoAdicao
                ? "Adicionar encontros"
                : isEdicao
                  ? "Confirmar edição"
                  : atividade.criarNoticia
                    ? "Cadastrar Notícia"
                    : "Prosseguir cadastro"
          }}
        </button>
      </div>
    </div>
  </div>

  <ModalDeConfirmacao
    v-if="modalConfirmacao.mostrar"
    :texto="modalConfirmacao.texto"
    @confirmacao="tratarConfirmacaoCadastro"
  />

  <Aviso
    v-if="aviso.mostrar"
    :sucesso="aviso.sucesso"
    :texto="aviso.mensagem"
    @fecharModal="aviso.mostrar = false"
  />
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRouter, useRoute, onBeforeRouteLeave } from "vue-router";

import Breadcrumb from "@/components/common/Breadcrumb.vue";
import ModalDeConfirmacao from "@/components/common/modalDeConfirmacao.vue";
import Aviso from "@/components/common/aviso.vue";
import FormEncontroInputs from "./FormEncontroInputs.vue";
import ListaEncontrosAdicionados from "./ListaEncontrosAdicionados.vue";

import { useAtividades } from "@/composables/useAtividades";
import { useEncontros } from "@/composables/useEncontros";
import { useValidacaoEncontros } from "@/composables/useValidacaoEncontros";
import acaoService from "@/service/acaoService";
import { criarMensagemErro } from "@/utils/FormatarEntradas.js";
import {
  setCadastroAtividadeTemp,
  getCadastroAtividadeTemp,
  clearCadastroAtividadeTemp,
  setAtividadeEmCadastro,
  clearAtividadeEmCadastro,
} from "@/utils/atividadeCadastroStorage";

const props = defineProps({
  acaoId: {
    type: String,
    default: "",
  },
  atividadeId: {
    type: String,
    default: "",
  },
  dadosAtividade: {
    type: Object,
    default: null,
  },
  vagasLimitadas: {
    type: Boolean,
    default: false,
  },
  modoAdicao: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const route = useRoute();
const { saveAtividade, fetchAtividadeById, isSaving } = useAtividades();
const {
  fetchEncontros,
  createEncontro,
  createMuitosEncontros,
  updateEncontro,
  deleteEncontro,
  isLoading: isLoadingEncontros,
  isSaving: isSavingEncontros,
} = useEncontros();
const {
  criarEncontroPadrao,
  temEdicaoPendente,
  extrairDuracaoEncontro,
  calcularFimPeriodo,
  formatarEncontro,
  formatarEncontroParaEnvio,
  validarDuracao,
  validarDatasEncontros,
  calcularDataMinima,
  toUTC,
} = useValidacaoEncontros();

function carregarDadosIniciaisAtividade() {
  if (props.dadosAtividade && props.dadosAtividade.nome) {
    return { ...props.dadosAtividade };
  }
  const parsed = getCadastroAtividadeTemp();
  if (parsed) {
    return parsed;
  }
  return {};
}

const atividade = ref(carregarDadosIniciaisAtividade());
const encontroAtual = ref(criarEncontroPadrao());
const encontrosAdicionados = ref(
  !props.modoAdicao && Array.isArray(atividade.value.encontros)
    ? atividade.value.encontros.map(formatarEncontro)
    : []
);
const encontrosExistentes = ref([]);
const encontrosOriginais = ref(
  Array.isArray(atividade.value.encontrosOriginais)
    ? atividade.value.encontrosOriginais.map(formatarEncontro)
    : []
);

const targetAtividadeId = computed(
  () =>
    props.atividadeId ||
    route.params.atividadeId ||
    atividade.value?.id ||
    atividade.value?.atividadeId ||
    ""
);
const isEdicao = computed(() => Boolean(targetAtividadeId.value));

const modalConfirmacao = ref({
  mostrar: false,
  texto: "",
});

const aviso = ref({
  mostrar: false,
  mensagem: "",
  sucesso: false,
});

const dataMinima = computed(() =>
  calcularDataMinima(atividade.value?.dataFimInscricao, [
    ...encontrosExistentes.value,
    ...encontrosAdicionados.value,
  ])
);

const breadcrumbItems = computed(() => {
  const targetAcaoId =
    props.acaoId || route?.params?.id || atividade.value?.acaoExtensaoId;

  if (props.modoAdicao) {
    return [
      { label: "Início", to: "/" },
      { label: "Ações", to: "/suas-acoes" },
      { tipo: "acao", id: targetAcaoId, to: `/acao/${targetAcaoId}` },
      { label: "Atividades", to: `/acao/${targetAcaoId}/atividades-acao` },
      {
        label: atividade.value?.nome || "Atividade",
        to: `/acao/${targetAcaoId}/atividades/${targetAtividadeId.value}`,
      },
      { label: "Adicionar Encontros", to: "" },
    ];
  }

  if (isEdicao.value) {
    return [
      { label: "Início", to: "/" },
      { label: "Ações", to: "/suas-acoes" },
      { tipo: "acao", id: targetAcaoId, to: `/acao/${targetAcaoId}` },
      { label: "Atividades", to: `/acao/${targetAcaoId}/atividades-acao` },
      {
        label: atividade.value?.nome || "Atividade",
        to: `/acao/${targetAcaoId}/atividades/${targetAtividadeId.value}`,
      },
      {
        label: "Editar Atividade",
        to: `/acao/${targetAcaoId}/editar-atividade/${targetAtividadeId.value}`,
      },
      { label: "Editar Encontros", to: "" },
    ];
  }

  return [
    { label: "Início", to: "/" },
    { label: "Ações", to: "/suas-acoes" },
    { tipo: "acao", id: targetAcaoId, to: `/acao/${targetAcaoId}` },
    { label: "Atividades", to: `/acao/${targetAcaoId}/atividades-acao` },
    {
      label: "Cadastrar Atividade",
      to: `/acao/${targetAcaoId}/cadastrar-atividade`,
    },
    { label: "Cadastrar Encontro", to: "" },
  ];
});

const resetarEncontroAtual = () => {
  encontroAtual.value = criarEncontroPadrao();
};

function salvarEstadoTemporario() {
  if (props.modoAdicao) return;
  const dadosAtividade = getCadastroAtividadeTemp() || {};

  const payloadAtualizado = {
    ...dadosAtividade,
    ...atividade.value,
    encontros: encontrosAdicionados.value,
    encontrosOriginais: encontrosOriginais.value,
  };

  setCadastroAtividadeTemp(payloadAtualizado);
}

function validarDatasDosEncontros() {
  const encontrosParaValidar = isEdicao.value
    ? encontrosAdicionados.value.filter(
        (e) => !e.encontroId || encontroFoiModificado(e)
      )
    : encontrosAdicionados.value;

  const resultado = validarDatasEncontros(
    encontrosParaValidar,
    atividade.value?.dataFimInscricao
  );
  if (!resultado.valido) {
    aviso.value = {
      mostrar: true,
      sucesso: false,
      mensagem: resultado.mensagem,
    };
    return false;
  }
  return true;
}

function adicionarEncontro() {
  const validacaoDuracao = validarDuracao(
    encontroAtual.value.duracaoHoras,
    encontroAtual.value.duracaoMinutos
  );

  if (!validacaoDuracao.valido) {
    aviso.value = {
      mostrar: true,
      sucesso: false,
      mensagem: validacaoDuracao.mensagem,
    };
    return;
  }

  const { duracaoHoras, duracaoMinutos, duracao } = extrairDuracaoEncontro(
    encontroAtual.value
  );
  const fimPeriodo = calcularFimPeriodo(
    encontroAtual.value.inicio_periodo,
    duracaoHoras,
    duracaoMinutos
  );

  encontrosAdicionados.value.push({
    ...encontroAtual.value,
    duracaoHoras,
    duracaoMinutos,
    duracao,
    fim_periodo: fimPeriodo,
  });

  salvarEstadoTemporario();
  resetarEncontroAtual();
}

function editarEncontro(encontro, index) {
  if (!temEdicaoPendente(encontroAtual.value)) {
    encontroAtual.value = formatarEncontro(encontro);
    removerEncontro(index);
  } else {
    aviso.value = {
      mostrar: true,
      sucesso: false,
      mensagem: "Conclua a edição do encontro atual antes de editar outro.",
    };
  }
}

function removerEncontro(index) {
  encontrosAdicionados.value.splice(index, 1);
  salvarEstadoTemporario();
}

function voltar() {
  salvarEstadoTemporario();

  if (window.history.length > 1) {
    router.back();
  } else {
    const targetAcaoId =
    props.acaoId ||
    route.params.id ||
    atividade.value?.acaoExtensaoId ||
    atividade.value?.acaoId;
    if (targetAcaoId) {
      if (isEdicao.value) {
        router.push({
          name: "editar-atividade",
          params: { id: targetAcaoId, atividadeId: targetAtividadeId.value },
        });
      } else {
        router.push({
          name: "cadastrar-atividade",
          params: { id: targetAcaoId },
        });
      }
    }
  }
}

async function carregarEncontrosDaAtividade(atividadeId) {
  if (!atividadeId) return false;
  try {
    const listaEncontros = await fetchEncontros(0, 1000, atividadeId);
    if (Array.isArray(listaEncontros)) {
      const formatados = listaEncontros.map(formatarEncontro);
      encontrosOriginais.value = JSON.parse(JSON.stringify(formatados));
      if (props.modoAdicao) {
        encontrosExistentes.value = formatados;
        encontrosAdicionados.value = [];
        return true;
      }
      encontrosAdicionados.value = formatados;
      salvarEstadoTemporario();
      return true;
    }
  } catch (err) {
    console.error("Erro ao buscar encontros da atividade:", err);
  }
  return false;
}

async function restaurarEncontrosOriginais() {
  if (!targetAtividadeId.value) return;
  const sucesso = await carregarEncontrosDaAtividade(targetAtividadeId.value);
  if (sucesso) {
    resetarEncontroAtual();
    aviso.value = {
      mostrar: true,
      sucesso: true,
      mensagem: "Encontros originais restaurados com sucesso!",
    };
  } else {
    aviso.value = {
      mostrar: true,
      sucesso: false,
      mensagem: "Erro ao buscar os encontros originais da atividade.",
    };
  }
}

function cadastrarEncontros() {
  if (temEdicaoPendente(encontroAtual.value)) {
    aviso.value = {
      mostrar: true,
      sucesso: false,
      mensagem: "Adicione ou conclua o encontro atual antes de prosseguir.",
    };
    return;
  }

  if (encontrosAdicionados.value.length === 0) {
    aviso.value = {
      mostrar: true,
      sucesso: false,
      mensagem:
        "Adicione pelo menos um encontro para prosseguir com o cadastro.",
    };
    return;
  }

  if (!validarDatasDosEncontros()) {
    return;
  }

  modalConfirmacao.value = {
    mostrar: true,
    texto: props.modoAdicao
      ? "Os novos encontros serão adicionados à atividade. Deseja continuar?"
      : isEdicao.value
      ? "Ao confirmar os encontros você irá atualizar os encontros da atividade, tem certeza que deseja prosseguir com a ação?"
      : atividade.value?.criarNoticia
        ? "Ao salvar os encontros você irá prosseguir para o cadastro da notícia, tem certeza que deseja continuar?"
        : "Ao cadastrar os encontros você irá avançar no cadastro da atividade, tem certeza que deseja prosseguir com a ação?",
  };
}

const STATUS_NAO_EDITAVEIS = ["FINALIZADO", "CANCELADO", "EM_EXECUCAO"];

function encontroFoiModificado(atual) {
  const original = encontrosOriginais.value.find(
    (o) => o.encontroId === atual.encontroId
  );
  if (!original) return false;
  if (STATUS_NAO_EDITAVEIS.includes(original.status)) return false;
  return (
    (atual.titulo || "") !== (original.titulo || "") ||
    (atual.descricao || "") !== (original.descricao || "") ||
    (atual.inicio_periodo || "") !== (original.inicio_periodo || "") ||
    (atual.fim_periodo || "") !== (original.fim_periodo || "")
  );
}

async function cadastrarNovosEncontros(targetId) {
  const novosEncontros = encontrosAdicionados.value.filter(
    (e) => !e.encontroId
  );
  if (novosEncontros.length === 1) {
    await createEncontro(
      targetId,
      formatarEncontroParaEnvio(novosEncontros[0])
    );
  } else if (novosEncontros.length > 1) {
    const payloadLista = novosEncontros.map(formatarEncontroParaEnvio);
    await createMuitosEncontros(targetId, payloadLista);
  }
}

async function salvarEncontrosEdicao() {
  const targetId = targetAtividadeId.value;

  // Modo "adicionar": somente cria os novos encontros, nunca reenvia os existentes
  if (props.modoAdicao) {
    await cadastrarNovosEncontros(targetId);
    return;
  }

  // 1. Deleta encontros originais que foram removidos (exceto não editáveis)
  const encontrosParaDeletar = encontrosOriginais.value.filter((orig) => {
    return (
      orig.encontroId &&
      !STATUS_NAO_EDITAVEIS.includes(orig.status) &&
      !encontrosAdicionados.value.some(
        (atual) => atual.encontroId === orig.encontroId
      )
    );
  });
  for (const enc of encontrosParaDeletar) {
    await deleteEncontro(enc.encontroId);
  }

  // 2. Atualiza apenas os encontros existentes que foram modificados
  const encontrosParaAtualizar = encontrosAdicionados.value.filter(
    (e) => e.encontroId && encontroFoiModificado(e)
  );
  for (const enc of encontrosParaAtualizar) {
    await updateEncontro(enc.encontroId, formatarEncontroParaEnvio(enc));
  }

  // 3. Cadastra novos encontros adicionados
  await cadastrarNovosEncontros(targetId);
}

async function tratarConfirmacaoCadastro(confirmado) {
  modalConfirmacao.value.mostrar = false;
  if (!confirmado) return;

  if (!validarDatasDosEncontros()) {
    return;
  }

  const targetAcaoId =
    props.acaoId ||
    route.params.id ||
    atividade.value?.acaoExtensaoId ||
    atividade.value?.acaoId;

  try {
    if (isEdicao.value) {
      await salvarEncontrosEdicao();
      clearCadastroAtividadeTemp();
      router.push(
        `/acao/${targetAcaoId}/atividades/${targetAtividadeId.value}`
      );
    } else {
      if (atividade.value.imagem?.base64Data === "") {
        atividade.value.imagem = null;
      }
      const payload = {
        ...atividade.value,
        encontros: encontrosAdicionados.value.map(formatarEncontroParaEnvio),
        dataInicioRealizacao: null,
        dataFimRealizacao: null,
        dataInicioInscricao:
          atividade.value.modalidadeInscricao === "NESCESSITA_INSCRICAO" &&
          atividade.value.dataInicioInscricao
            ? toUTC(atividade.value.dataInicioInscricao)
            : null,
        dataFimInscricao:
          atividade.value.modalidadeInscricao === "NESCESSITA_INSCRICAO" &&
          atividade.value.dataFimInscricao
            ? toUTC(atividade.value.dataFimInscricao)
            : null,
      };

      // Fallback de imagem da ação pai caso a atividade não tenha imagem própria
      if (!payload.imagem || !payload.imagem.base64Data) {
        try {
          if (targetAcaoId) {
            const dadosAcao = await acaoService.getAcoesById(targetAcaoId);
            if (dadosAcao?.imagem?.base64Data) {
              payload.imagem = {
                mimeType: dadosAcao.imagem.mimeType || "image/png",
                base64Data: dadosAcao.imagem.base64Data,
                descricaoImagem:
                  dadosAcao.descricaoImagem ||
                  `Imagem da ação ${dadosAcao.titulo || ""}`,
                creditosImagem: dadosAcao.imagem.creditosImagem || "",
              };
            } else if (dadosAcao?.imagemUrl) {
              payload.imagemUrl = dadosAcao.imagemUrl;
              payload.descricaoImagem =
                dadosAcao.descricaoImagem ||
                `Imagem da ação ${dadosAcao.titulo || ""}`;
              payload.imagem = null;
            } else {
              payload.imagem = null;
            }
          } else {
            payload.imagem = null;
          }
        } catch (e) {
          console.error("Erro ao buscar imagem padrão da ação:", e);
          payload.imagem = null;
        }
      }

      if (payload.criarNoticia) {
        setAtividadeEmCadastro(payload);
        clearCadastroAtividadeTemp();
        await router.push({
          name: "cadastrar-noticia",
          params: { id: targetAcaoId },
        });
        return;
      }

      const atividadeSalva = await saveAtividade(payload);
      clearCadastroAtividadeTemp();
      clearAtividadeEmCadastro();

      const finalAtividadeId = atividadeSalva?.id || targetAtividadeId.value;
      router.push(`/acao/${targetAcaoId}/atividades/${finalAtividadeId}`);
    }
  } catch (error) {
    console.error("Ocorreu um erro ao salvar encontros:", error);
    let mensagemErro = isEdicao.value
      ? "Ocorreu um erro ao atualizar os encontros da atividade: "
      : "Ocorreu um erro ao cadastrar a atividade com os encontros: ";

    let erro = mensagemErro + criarMensagemErro(error);
    aviso.value = {
      mostrar: true,
      sucesso: false,
      mensagem: erro,
    };
  }
}

watch(
  () => props.dadosAtividade,
  (novosDados) => {
    if (novosDados && Object.keys(novosDados).length > 0) {
      atividade.value = { ...atividade.value, ...novosDados };
      if (novosDados.encontros?.length) {
        encontrosAdicionados.value = novosDados.encontros.map(formatarEncontro);
      }
    }
  },
  { deep: true }
);

const rotasDoFluxo = [
  "cadastrar-atividade",
  "cadastrar-encontros-atividade",
  "editar-atividade",
  "editar-encontros-atividade",
  "cadastrar-noticia",
];

onBeforeRouteLeave((to) => {
  if (!rotasDoFluxo.includes(to.name)) {
    sessionStorage.removeItem("cadastro-atividade-temp");
  }
});

async function carregarDadosAtividade(atividadeId) {
  if (!atividadeId) return;
  try {
    const dados = await fetchAtividadeById(atividadeId);
    if (dados) {
      atividade.value = { ...dados };
    }
  } catch (e) {
    console.error("Erro ao buscar dados da atividade:", e);
  }
}

onMounted(async () => {
  if (isEdicao.value && targetAtividadeId.value) {
    if (!atividade.value || !atividade.value.nome) {
      await carregarDadosAtividade(targetAtividadeId.value);
    }

    if (props.modoAdicao || encontrosAdicionados.value.length === 0) {
      await carregarEncontrosDaAtividade(targetAtividadeId.value);
    }
    return;
  }

  if (!atividade.value || !atividade.value.nome) {
    const targetAcaoId =
    props.acaoId ||
    route.params.id ||
    atividade.value?.acaoExtensaoId ||
    atividade.value?.acaoId;
    if (targetAcaoId) {
      router.push({
        name: "cadastrar-atividade",
        params: { id: targetAcaoId },
      });
    }
  }
});
</script>

<style scoped>
.cadastro-form-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 15px;
  margin-top: 1.5rem;
}

.voltar-btn {
  background-color: var(--branco);
  color: var(--roxo-primario);
  border: 1px solid var(--roxo-primario);
  padding: 8px 24px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.voltar-btn:hover {
  background-color: var(--roxo-superficie);
}

.restaurar-btn {
  background-color: var(--branco);
  color: var(--laranja-escuro);
  padding: 8px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  white-space: nowrap;
}

.restaurar-btn:hover {
  background-color: var(--laranja-claro);
}

.restaurar-btn:disabled,
.voltar-btn:disabled,
.cadastro-form > button:disabled {
  background-color: var(--cinza-medio) !important;
  color: var(--branco) !important;
  border-color: transparent !important;
  cursor: not-allowed !important;
  opacity: 0.7;
}
</style>
